import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import WomanIcon from '../images/brands-unique/gfa/badge-woman.svg';
import filtersTypes from '../constants/filtersTypes';
import useWhitelabelEnvs from './whitelabel/useWhitelabelEnvs';
import {
  PCD_SLOT_CATEGORY_KEYS,
  resolvePassengerCategory,
  getResultPassengerCategoryLabelType,
  requiresPassengerCategoryQuantity,
} from '../utils/passengerCategories';

const { CATEGORIES } = filtersTypes;

const categoriesIconTypes = {
  general: 'emojiAdult',
  older: 'emojiAdult',
  student: 'emojiStudent',
  wheelchair_handicap: 'emojiHandshake',
  special: 'emojiHandshake',
  disability: 'emojiHandshake',
  free_pass: 'emojiHandshake',
  teacher: 'emojiTeacher',
  minor: 'emojiBoy',
  intermunicipal_free_pass: 'emojiAdult',
};

const categoriesI18Keys = {
  general: 'passengers:type.general',
  older: 'passengers:type.older',
  student: 'passengers:type.student',
  wheelchair_handicap: 'passengers:type.wheelchair_handicap',
  special: 'passengers:type.special',
  disability: 'passengers:type.disability',
  free_pass: 'passengers:type.free_pass',
  teacher: 'passengers:type.teacher',
  minor: 'passengers:type.minor',
  pet_friendly: 'passengers:type.pet_friendly',
  support_woman: 'passengers:type.support_woman',
  intermunicipal_free_pass: 'passengers:type.intermunicipal_free_pass',
};

/**
 * Hook to get the available categories.
 * @param {String} way - Way of the trips to filter
 * @returns Array
 */
const useAvailableCategories = (way) => {
  const { t, i18n } = useTranslation('');
  const { brand } = useWhitelabelEnvs();
  const state = useSelector((state) => state);
  const {
    search,
    trips,
    whitelabelConfig: { features },
  } = state;
  const searchId = search.getIn([way, 'id']);
  const searchPassengers = search.get('passengers');
  const selectedCategories = state.tripFilters.getIn(['filters', 'categories']);
  const tripListFiltered = useMemo(() => {
    const tripsList = trips?.getIn([searchId, 'buses', 'trips']);
    return tripsList?.filter((t) => !t.get('openTicket'))?.toJS() || [];
  }, [trips, searchId]);

  const categories = useMemo(() => {
    if (!tripListFiltered.length) return [];

    const tripCategories = tripListFiltered.map((trip) => trip.passengerTypes);
    const categoryTypes = new Set(
      tripCategories.flatMap((categories) => categories.map(({ type }) => type)),
    );
    PCD_SLOT_CATEGORY_KEYS.forEach((type) => {
      if (searchPassengers?.get(type) > 0 || selectedCategories?.includes(type)) {
        categoryTypes.add(type);
      }
    });

    const baseCategories = Array.from(categoryTypes, (type) => ({ type }))
      .filter((category) => category.type !== 'general') // Exclude 'general' category as it's always available
      .filter((category) => {
        // If SPECIFIC_CATEGORIES is empty, show all categories
        // If SPECIFIC_CATEGORIES has elements, show only those categories
        return (
          features.SPECIFIC_CATEGORIES.length === 0 ||
          features.SPECIFIC_CATEGORIES.includes(category.type) ||
          tripCategories.some((categories) => {
            return features.SPECIFIC_CATEGORIES.includes(
              resolvePassengerCategory(category.type, categories),
            );
          })
        );
      })
      .map((category) => {
        const canUse =
          category.type === 'pet_friendly'
            ? features.SPECIAL_CATEGORIES.includes('pet_friendly')
            : true;

        const required = requiresPassengerCategoryQuantity(category.type, brand)
          ? searchPassengers?.get(category.type) || 1
          : 1;
        const hasAvailability = tripCategories.some((categories) => {
          const resolved = resolvePassengerCategory(category.type, categories);
          return categories.find(({ type }) => type === resolved)?.availability >= required;
        });

        const labelType = getResultPassengerCategoryLabelType(category.type, brand, i18n?.language);

        return {
          ...category,
          icon: categoriesIconTypes[category.type],
          i18Key: categoriesI18Keys[labelType],
          label: t(`passengers:type.${labelType}`, { context: brand }),
          hasAvailability,
          canUse,
          value: category.type,
          filterType: CATEGORIES,
        };
      });

    if (features.SHOW_WOMEN_ONLY_SEAT_FILTER) {
      const womanSupport = {
        type: 'supportWoman',
        i18Key: categoriesI18Keys.support_woman,
        label: t(`seats:seat_woman`),
        icon: WomanIcon,
        hasAvailability: Boolean(tripListFiltered.find((trip) => trip.supportWoman)),
        canUse: features.WOMAN_SEATS_ENABLED,
        value: 'supportWoman',
        filterType: CATEGORIES,
      };

      return [...baseCategories, womanSupport];
    }

    return baseCategories;
  }, [tripListFiltered, features, t, brand, i18n?.language, searchPassengers, selectedCategories]);

  return categories;
};

export default useAvailableCategories;
